import { Fraction } from '@learnforge/demo-math/numeric';
import { type Expr, num, variable, add, mul, pow, equals, canonicalise } from './expr';
import { parse } from './parse';

export type SequenceKind = 'arithmetic' | 'geometric' | 'recurrence';

export interface ArithmeticSpec {
  kind: 'arithmetic';
  a1: Fraction;
  d: Fraction;
}

export interface GeometricSpec {
  kind: 'geometric';
  a1: Fraction;
  r: Fraction;
}

export interface LinearRecurrenceSpec {
  kind: 'recurrence';
  a1: Fraction;
  c: Fraction;
  d: Fraction;
}

export type SequenceSpec = ArithmeticSpec | GeometricSpec | LinearRecurrenceSpec;

export function nthTerm(spec: SequenceSpec, n: number): Fraction {
  switch (spec.kind) {
    case 'arithmetic': {
      // a_n = a1 + (n-1)*d
      return spec.a1.add(new Fraction(n - 1, 1).mul(spec.d));
    }
    case 'geometric': {
      // a_n = a1 * r^(n-1)
      const power = spec.r.pow(n - 1);
      return spec.a1.mul(power);
    }
    case 'recurrence': {
      // a_{i+1} = c * a_i + d, iterate (n-1) times from a1
      let current = spec.a1;
      for (let i = 0; i < n - 1; i += 1) {
        current = spec.c.mul(current).add(spec.d);
      }
      return current;
    }
  }
}

export function partialSum(spec: SequenceSpec, n: number): Fraction {
  switch (spec.kind) {
    case 'arithmetic': {
      // S_n = n/2 * (2*a1 + (n-1)*d)
      const two = new Fraction(2, 1);
      const nf = new Fraction(n, 1);
      const part1 = two.mul(spec.a1);
      const part2 = new Fraction(n - 1, 1).mul(spec.d);
      const sum = part1.add(part2);
      return nf.mul(sum).div(two);
    }
    case 'geometric': {
      if (spec.r.equals(Fraction.ONE)) {
        // S_n = n * a1
        return new Fraction(n, 1).mul(spec.a1);
      }
      // S_n = a1 * (1 - r^n) / (1 - r)
      const one = Fraction.ONE;
      const rn = spec.r.pow(n);
      const numerator = one.sub(rn);
      const denominator = one.sub(spec.r);
      return spec.a1.mul(numerator).div(denominator);
    }
    case 'recurrence': {
      // Sum the iterated terms
      let sum = Fraction.ZERO;
      for (let i = 1; i <= n; i += 1) {
        sum = sum.add(nthTerm(spec, i));
      }
      return sum;
    }
  }
}

export function convergenceLimit(spec: SequenceSpec): Fraction | null {
  switch (spec.kind) {
    case 'arithmetic': {
      // Converges only if d=0 and a1=0 (sum is 0).
      if (spec.d.equals(Fraction.ZERO)) {
        if (spec.a1.equals(Fraction.ZERO)) {
          return Fraction.ZERO;
        }
      }
      return null;
    }
    case 'geometric': {
      // Converges iff |r| < 1, limit is a1 / (1 - r)
      if (spec.r.abs.cmp(Fraction.ONE) < 0) {
        const one = Fraction.ONE;
        const denominator = one.sub(spec.r);
        return spec.a1.div(denominator);
      }
      return null;
    }
    case 'recurrence': {
      // Not guaranteed to converge; return null
      return null;
    }
  }
}

export function telescopingDecompose(
  expr: Expr,
  kVar: string,
): { partial: Expr; collapsed: Expr } | null {
  // Recognize 1/(k*(k+1)) -> { partial: 1/k - 1/(k+1), collapsed: 1 - 1/(n+1) }
  const expected = parse(`1/(${kVar}*(${kVar}+1))`, { variableSet: new Set([kVar, 'n']) });
  if (equals(canonicalise(expr), canonicalise(expected))) {
    const partial = parse(`1/${kVar} - 1/(${kVar}+1)`, { variableSet: new Set([kVar, 'n']) });
    const collapsed = parse(`1 - 1/(n+1)`, { variableSet: new Set([kVar, 'n']) });
    return { partial, collapsed };
  }
  return null;
}

export function recurrenceToClosedForm(spec: LinearRecurrenceSpec, varName: string): Expr {
  // For a_{n+1} = c*a_n + d:
  // if c == 1: a_n = a1 + (n-1)*d
  // else: L = d/(1-c), b1 = a1 - L, a_n = b1*c^(n-1) + L

  if (spec.c.equals(Fraction.ONE)) {
    // Arithmetic case
    return add(num(spec.a1), mul(add(variable(varName), num(-1)), num(spec.d)));
  }

  // General recurrence case
  const one = Fraction.ONE;
  const L = spec.d.div(one.sub(spec.c));
  const b1 = spec.a1.sub(L);

  // Build Expr: add(mul(num(b1), pow(num(c), add(variable(varName), num(-1)))), num(L))
  return add(mul(num(b1), pow(num(spec.c), add(variable(varName), num(-1)))), num(L));
}

export function closedFormExpr(spec: SequenceSpec, varName: string): Expr {
  switch (spec.kind) {
    case 'arithmetic': {
      // a_n = a1 + (n-1)*d
      return add(num(spec.a1), mul(add(variable(varName), num(-1)), num(spec.d)));
    }
    case 'geometric': {
      // a_n = a1 * r^(n-1)
      return mul(num(spec.a1), pow(num(spec.r), add(variable(varName), num(-1))));
    }
    case 'recurrence': {
      return recurrenceToClosedForm(spec, varName);
    }
  }
}

export function partialSumClosedForm(spec: SequenceSpec, varName: string): Expr {
  switch (spec.kind) {
    case 'arithmetic': {
      // S_n = n/2 * (2*a1 + (n-1)*d)
      // mul(mul(num(1/2), variable(varName)), add(mul(num(2), num(a1)), mul(add(variable(varName), num(-1)), num(d))))
      return mul(
        mul(num(new Fraction(1, 2)), variable(varName)),
        add(
          mul(num(new Fraction(2, 1)), num(spec.a1)),
          mul(add(variable(varName), num(-1)), num(spec.d)),
        ),
      );
    }
    case 'geometric': {
      if (spec.r.equals(Fraction.ONE)) {
        // S_n = n * a1
        return mul(variable(varName), num(spec.a1));
      }
      // S_n = a1 * (1 - r^n) / (1 - r)
      // mul(num(a1), mul(add(num(1), mul(num(-1), pow(num(r), variable(varName)))), pow(add(num(1), mul(num(-1), num(r))), num(-1))))
      return mul(
        num(spec.a1),
        mul(
          add(
            num(Fraction.ONE),
            mul(num(new Fraction(-1, 1)), pow(num(spec.r), variable(varName))),
          ),
          pow(add(num(Fraction.ONE), mul(num(new Fraction(-1, 1)), num(spec.r))), num(-1)),
        ),
      );
    }
    case 'recurrence': {
      // For recurrence, use the closed form and sum it (not a simple closed form for sum)
      // For now, return the partial sum as a sigma notation or just use the recursive definition
      // This is mathematically complex, so we'll compute it iteratively via nthTerm
      throw new Error('partialSumClosedForm not implemented for recurrence sequences');
    }
  }
}
